import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SilhouetteChartProps {
  data: { label: string; value: number }[];
}

const traitColors: Record<string, { hue: string }> = {
  Analytical: { hue: '220 80% 55%' },
  Creative: { hue: '280 70% 60%' },
  Communication: { hue: '35 90% 55%' },
  Leadership: { hue: '0 70% 55%' },
  Execution: { hue: '145 60% 45%' },
  Technical: { hue: '190 75% 50%' },
};

const traitDescriptions: Record<string, string> = {
  Analytical: 'Your ability to break down complex problems, spot patterns, and think logically.',
  Creative: 'Your imagination, originality, and ability to see possibilities others miss.',
  Communication: 'How effectively you express ideas, persuade, and connect with others.',
  Leadership: 'Your drive to guide, inspire, and take ownership of outcomes.',
  Execution: 'Your ability to plan, organize, and get things done reliably.',
  Technical: 'Your aptitude for systems, tools, and building with precision.',
};

const traitPositions: Record<string, { x: number; y: number }> = {
  Analytical: { x: 200, y: 60 },
  Creative: { x: 200, y: 105 },
  Communication: { x: 200, y: 165 },
  Leadership: { x: 200, y: 230 },
  Technical: { x: 115, y: 280 },
  Execution: { x: 285, y: 280 },
};

// Organic splash blob paths — each is unique
const splashPaths: Record<string, string[]> = {
  Analytical: [
    'M0,-1 C0.4,-0.9 0.8,-0.5 0.95,-0.1 C1.1,0.3 0.7,0.8 0.3,0.95 C-0.1,1.1 -0.6,0.7 -0.9,0.3 C-1.1,-0.1 -0.5,-0.8 0,-1Z',
    'M0.1,-0.95 C0.6,-0.7 1.0,-0.2 0.9,0.2 C0.8,0.7 0.3,1.0 -0.2,0.9 C-0.7,0.8 -1.0,0.3 -0.9,-0.15 C-0.8,-0.6 -0.4,-1.1 0.1,-0.95Z',
  ],
  Creative: [
    'M0,-1.05 C0.5,-0.8 0.9,-0.3 1.0,0.15 C1.05,0.6 0.6,1.0 0.1,0.95 C-0.4,0.9 -0.8,0.5 -0.95,0.05 C-1.1,-0.4 -0.5,-0.95 0,-1.05Z',
    'M-0.1,-0.9 C0.35,-1.05 0.85,-0.6 0.95,-0.1 C1.05,0.4 0.65,0.9 0.15,1.0 C-0.35,1.05 -0.85,0.6 -0.95,0.1 C-1.05,-0.4 -0.55,-0.85 -0.1,-0.9Z',
  ],
  Communication: [
    'M0.05,-1.0 C0.55,-0.85 0.95,-0.35 0.9,0.15 C0.85,0.65 0.4,1.05 -0.1,0.95 C-0.6,0.85 -0.95,0.35 -0.85,-0.15 C-0.75,-0.65 -0.35,-1.05 0.05,-1.0Z',
    'M0,-0.95 C0.45,-0.9 0.9,-0.45 0.95,0.05 C1.0,0.55 0.55,0.95 0.05,0.9 C-0.45,0.85 -0.9,0.45 -0.95,-0.05 C-1.0,-0.55 -0.5,-0.95 0,-0.95Z',
  ],
  Leadership: [
    'M0.1,-0.95 C0.6,-0.75 1.0,-0.25 0.95,0.25 C0.9,0.75 0.4,1.05 -0.1,0.9 C-0.6,0.75 -0.95,0.25 -0.85,-0.2 C-0.75,-0.7 -0.3,-1.05 0.1,-0.95Z',
    'M-0.05,-1.0 C0.5,-0.8 0.85,-0.3 0.9,0.2 C0.95,0.7 0.5,1.0 0,0.95 C-0.5,0.9 -0.9,0.4 -0.9,-0.1 C-0.9,-0.6 -0.5,-1.0 -0.05,-1.0Z',
  ],
  Execution: [
    'M0,-1.0 C0.5,-0.85 0.9,-0.35 0.85,0.15 C0.8,0.65 0.35,1.0 -0.15,0.9 C-0.65,0.8 -0.95,0.3 -0.85,-0.2 C-0.75,-0.7 -0.4,-1.05 0,-1.0Z',
    'M0.1,-0.9 C0.55,-0.75 0.9,-0.3 0.85,0.2 C0.8,0.7 0.35,0.95 -0.15,0.85 C-0.6,0.75 -0.9,0.3 -0.8,-0.15 C-0.7,-0.6 -0.3,-0.95 0.1,-0.9Z',
  ],
  Technical: [
    'M0.05,-0.95 C0.55,-0.8 0.95,-0.3 0.9,0.2 C0.85,0.7 0.4,1.0 -0.1,0.9 C-0.6,0.8 -0.95,0.3 -0.9,-0.15 C-0.85,-0.65 -0.4,-1.0 0.05,-0.95Z',
    'M0,-1.0 C0.45,-0.9 0.85,-0.4 0.9,0.1 C0.95,0.6 0.5,0.95 0,0.9 C-0.5,0.85 -0.85,0.4 -0.9,-0.1 C-0.95,-0.6 -0.45,-0.95 0,-1.0Z',
  ],
};

const SilhouetteChart = ({ data }: SilhouetteChartProps) => {
  const [hoveredTrait, setHoveredTrait] = useState<string | null>(null);

  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-8 py-4 relative">
      <div className="relative">
      <svg width="340" height="380" viewBox="0 0 400 400">
        <defs>
          <clipPath id="bust-clip">
            <ellipse cx="200" cy="65" rx="42" ry="50" />
            <rect x="185" y="113" width="30" height="28" rx="10" />
            <path d="
              M 140 140 C 140 138, 155 135, 180 135 L 220 135 C 245 135, 260 138, 260 140
              L 268 180 L 272 230 C 274 270, 268 310, 260 340 L 250 370 L 150 370 L 140 340
              C 132 310, 126 270, 128 230 L 132 180 Z
            " />
            <path d="M 140 142 C 118 150, 100 170, 92 200 C 85 230, 88 260, 98 285 C 103 298, 110 308, 115 312" stroke="black" strokeWidth="28" fill="none" strokeLinecap="round" />
            <path d="M 260 142 C 282 150, 300 170, 308 200 C 315 230, 312 260, 302 285 C 297 298, 290 308, 285 312" stroke="black" strokeWidth="28" fill="none" strokeLinecap="round" />
          </clipPath>

          <filter id="soft-glow">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="splash-blur">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        {/* Silhouette outline */}
        <g opacity="0.25" fill="none" stroke="hsl(215 20% 70%)" strokeLinecap="round">
          <ellipse cx="200" cy="65" rx="42" ry="50" strokeWidth="1.5" />
          <rect x="185" y="113" width="30" height="28" rx="10" strokeWidth="1" />
          <path d="
            M 140 140 C 140 138, 155 135, 180 135 L 220 135 C 245 135, 260 138, 260 140
            L 268 180 L 272 230 C 274 270, 268 310, 260 340 L 250 370 L 150 370 L 140 340
            C 132 310, 126 270, 128 230 L 132 180 Z
          " strokeWidth="1.5" />
          <path d="M 140 142 C 118 150, 100 170, 92 200 C 85 230, 88 260, 98 285 C 103 298, 110 308, 115 312" strokeWidth="20" />
          <path d="M 260 142 C 282 150, 300 170, 308 200 C 315 230, 312 260, 302 285 C 297 298, 290 308, 285 312" strokeWidth="20" />
        </g>

        {/* Splash auras clipped to bust */}
        <g clipPath="url(#bust-clip)">
          {data.map((d, i) => {
            const pos = traitPositions[d.label];
            const color = traitColors[d.label];
            const paths = splashPaths[d.label];
            if (!pos || !color || !paths) return null;

            // Percentage drives everything: size, spread, opacity, pulse intensity
            const pct = d.value / 100;
            const baseScale = 15 + pct * 80; // 15 at 0%, 95 at 100% — huge range
            const baseOpacity = 0.15 + pct * 0.65; // faint at low, vivid at high
            const pulseRange = 0.08 + pct * 0.12; // stronger pulse for higher traits
            const pulseDuration = 4 - pct * 1.5; // faster pulse for stronger traits

            return (
              <g key={d.label} transform={`translate(${pos.x}, ${pos.y})`}>
                {/* Outer splash — large, diffused, heavily % dependent */}
                <motion.path
                  d={paths[0]}
                  fill={`hsl(${color.hue} / ${baseOpacity * 0.3})`}
                  filter="url(#splash-blur)"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: [baseScale * 1.6, baseScale * (1.6 + pulseRange), baseScale * 1.6],
                    opacity: [1, 1 - pulseRange, 1],
                  }}
                  transition={{
                    scale: { duration: pulseDuration, repeat: Infinity, ease: 'easeInOut' },
                    opacity: { duration: pulseDuration, repeat: Infinity, ease: 'easeInOut' },
                  }}
                  style={{ transformOrigin: '0 0' }}
                />
                {/* Mid splash */}
                <motion.path
                  d={paths[1]}
                  fill={`hsl(${color.hue} / ${baseOpacity * 0.5})`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: [baseScale * 1.0, baseScale * (1.0 + pulseRange * 0.8), baseScale * 1.0],
                    opacity: [1, 1 - pulseRange * 0.7, 1],
                  }}
                  transition={{
                    scale: { duration: pulseDuration * 0.85, repeat: Infinity, ease: 'easeInOut', delay: 0.3 },
                    opacity: { duration: pulseDuration * 0.85, repeat: Infinity, ease: 'easeInOut', delay: 0.3 },
                  }}
                  style={{ transformOrigin: '0 0' }}
                />
                {/* Core splash — vivid, tightest */}
                <motion.path
                  d={paths[0]}
                  fill={`hsl(${color.hue} / ${baseOpacity * 0.85})`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: [baseScale * 0.45, baseScale * (0.45 + pulseRange * 0.5), baseScale * 0.45],
                    opacity: [1, 1 - pulseRange * 0.4, 1],
                  }}
                  transition={{
                    scale: { duration: pulseDuration * 0.7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
                    opacity: { duration: pulseDuration * 0.7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
                  }}
                  style={{ transformOrigin: '0 0' }}
                />
              </g>
            );
          })}
        </g>

        {/* Core dots + percentage + hover targets */}
        {data.map((d, i) => {
          const pos = traitPositions[d.label];
          const color = traitColors[d.label];
          if (!pos || !color) return null;
          return (
            <g
              key={`dot-${d.label}`}
              onMouseEnter={() => setHoveredTrait(d.label)}
              onMouseLeave={() => setHoveredTrait(null)}
              className="cursor-pointer"
            >
              {/* Invisible larger hit area */}
              <circle cx={pos.x} cy={pos.y} r={28} fill="transparent" />
              <motion.circle
                cx={pos.x} cy={pos.y} r={14}
                fill={`hsl(${color.hue} / 0.4)`}
                stroke={`hsl(${color.hue} / 0.7)`}
                strokeWidth="1"
                filter="url(#soft-glow)"
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: i * 0.12 + 0.4, type: 'spring' }}
              />
              <motion.text
                x={pos.x} y={pos.y + 4.5}
                textAnchor="middle"
                className="font-display font-bold text-[11px] pointer-events-none"
                fill="white"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ delay: i * 0.12 + 0.55 }}
              >
                {d.value}%
              </motion.text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      <AnimatePresence>
        {hoveredTrait && (() => {
          const pos = traitPositions[hoveredTrait];
          const color = traitColors[hoveredTrait];
          const desc = traitDescriptions[hoveredTrait];
          const d = data.find(d => d.label === hoveredTrait);
          if (!pos || !color || !d) return null;
          // Convert SVG coords to pixel coords (svg is 400 viewbox, 340px wide)
          const pixelX = (pos.x / 400) * 340;
          const pixelY = (pos.y / 400) * 380;
          return (
            <motion.div
              key={hoveredTrait}
              initial={{ opacity: 0, y: 4, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute z-20 w-52 p-3 rounded-xl border border-border/50 bg-card shadow-xl pointer-events-none"
              style={{
                left: pixelX,
                top: pixelY - 80,
                transform: 'translateX(-50%)',
              }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: `hsl(${color.hue})`, boxShadow: `0 0 6px hsl(${color.hue} / 0.5)` }}
                />
                <span className="text-xs font-bold text-foreground">{hoveredTrait}</span>
                <span className="ml-auto text-xs font-display font-bold" style={{ color: `hsl(${color.hue})` }}>{d.value}%</span>
              </div>
              <p className="text-[10px] leading-relaxed text-muted-foreground">{desc}</p>
            </motion.div>
          );
        })()}
      </AnimatePresence>
      </div>

      {/* Side legend — ordered by body position top to bottom */}
      <div className="flex flex-col gap-3">
        {['Analytical', 'Creative', 'Communication', 'Leadership', 'Technical', 'Execution'].map((label, i) => {
          const d = data.find(d => d.label === label);
          const color = traitColors[label];
          if (!d || !color) return null;
          return (
            <motion.div
              key={label}
              className="flex items-center gap-3 cursor-pointer"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 + 0.4 }}
              onMouseEnter={() => setHoveredTrait(label)}
              onMouseLeave={() => setHoveredTrait(null)}
            >
              <div
                className="w-3.5 h-3.5 rounded-full shrink-0"
                style={{
                  backgroundColor: `hsl(${color.hue})`,
                  boxShadow: `0 0 10px hsl(${color.hue} / 0.6)`,
                }}
              />
              <div>
                <p className="text-sm font-semibold text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">{d.value}%</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default SilhouetteChart;
